/**
 * MaisonVisualizers — V14.63 Sprint 26F/G.
 *
 * Bloc multi-modes pour la visualisation 3D d'un projet Maison dans
 * l'onglet « 4. Validation » de l'Édition Plan V2.
 *
 * Modes disponibles :
 *   • 'svg'      — Composite SVG legacy (Sprint 26E, garde la rétro-compat)
 *   • 'shoebox'  — Shoebox 3D par étage (sélecteur d'étage)
 *   • 'building' — Bâtiment 3D empilé
 *   • 'exterior' — Vue extérieure 3D orbitable (terrain + façade + presets)
 *
 * Les données viennent de l'endpoint backend dédié au mode Maison V2 :
 *   GET /api/projects/{id}/maison/exterior  → footprint / terrain / extrusion
 *   GET /api/projects/{id}/floors           → floors (avec geometry) pour Shoebox/Bâtiment
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Box, Layers3, Satellite, Image as ImageIcon, Loader2, AlertCircle, RotateCw, Compass } from 'lucide-react';
import ExteriorPreview from '../../maison/ExteriorPreview';
import Shoebox3DViewer from '../../3d/Shoebox3DViewer';
import BuildingStack3DViewer from '../../3d/BuildingStack3DViewer';
import Exterior3DViewer from '../../3d/Exterior3DViewer';
import OrbitTourViewer from '../../3d/OrbitTourViewer';
import MaisonPdfExportButton from '../../3d/MaisonPdfExportButton';
import { formatApiError } from '../../../lib/formatApiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Convertit la liste des lots V2 (avec plan_source_editor.structure) en
 * floors compatibles avec nos composants 3D (qui attendent une géométrie
 * style micro-IFC en px). On suit la même logique que maison_exterior.py
 * côté backend pour denormaliser les coords normalisées [0..1] → px.
 */
const lotToFloor = (floor, lot) => {
  // Slot Mongo officiel utilisé par PUT /plan-source/structure.
  const pse = lot?.plan_source_editor || lot?.plan_source_state || {};
  const structure = pse.structure || {};
  const calibration = structure.calibration || {};
  const scaleMPerPx = +(calibration.m_per_px || 0) || 0;
  const transform = pse.transform || {};
  const imgW = +(transform.img_w || 1000) || 1000;
  const imgH = +(transform.img_h || 1000) || 1000;

  const walls = (structure.walls || []).map((w) => ({
    id:        String(w.id || ''),
    x1: (+w.x1 || 0) * imgW, y1: (+w.y1 || 0) * imgH,
    x2: (+w.x2 || 0) * imgW, y2: (+w.y2 || 0) * imgH,
    thickness: +w.thickness || 8,
    kind:      String(w.kind || 'cloison_fine'),
    // Sprint 30 — propagate the per-wall height override for mansarde.
    ...(Number.isFinite(+w.height_override_m) && +w.height_override_m > 0
      ? { height_override_m: +w.height_override_m }
      : {}),
  }));
  const terrain = (structure.terrain_polygon || []).map((v) => {
    const x = (Array.isArray(v) ? v[0] : v?.x) || 0;
    const y = (Array.isArray(v) ? v[1] : v?.y) || 0;
    return { x: x * imgW, y: y * imgH };
  });
  const stairs = (structure.stairs || []).map((s) => ({
    id: String(s.id || ''),
    x:  (+s.x || 0) * imgW, y:  (+s.y || 0) * imgH,
    w:  (+s.w || 0) * imgW, h:  (+s.h || 0) * imgH,
    kind: String(s.kind || 'droit'),
  }));
  // Sprint 31 — Lignes de brisure (mansardes).
  const skewEdges = (structure.skew_edges || []).map((e) => ({
    id: String(e.id || ''),
    x1: (+e.x1 || 0) * imgW, y1: (+e.y1 || 0) * imgH,
    x2: (+e.x2 || 0) * imgW, y2: (+e.y2 || 0) * imgH,
    ...(Number.isFinite(+e.low_side_hsp_m) && +e.low_side_hsp_m > 0
      ? { low_side_hsp_m: +e.low_side_hsp_m }
      : {}),
  }));
  // Sprint 31 Phase 2 — Velux (fenêtres de toit sur mansardes).
  const velux = (structure.velux || []).map((v) => ({
    id: String(v.id || ''),
    skewId: String(v.skewId || ''),
    t: Math.max(0, Math.min(1, +v.t || 0.5)),
    width_m:  Number.isFinite(+v.width_m)  ? +v.width_m  : 0.78,
    height_m: Number.isFinite(+v.height_m) ? +v.height_m : 1.18,
  }));

  return {
    floor_id: String(floor.floor_id || ''),
    name:     String(floor.name || ''),
    code:     String(floor.code || ''),
    level:    +floor.level || 0,
    // Sprint 30 — bring the true HSP (hsp_m) from the Édition-Plan flow AND
    // the height_m from the Single-House flow ; resolveFloorHeight() reads
    // both transparently. Fallback to 2.5 if neither is defined.
    hsp_m:    Number.isFinite(+floor.hsp_m) && +floor.hsp_m > 0 ? +floor.hsp_m : undefined,
    height_m: Number.isFinite(+floor.height_m) && +floor.height_m > 0 ? +floor.height_m : undefined,
    geometry: {
      scale_m_per_px: scaleMPerPx,
      walls, terrain, stairs,
      skew_edges: skewEdges,  // Sprint 31
      velux,                  // Sprint 31 Phase 2
      windows: [], doors: [],
      transform: pse.alignment_transform || null,
    },
  };
};

const MODE_OPTIONS = [
  { id: 'exterior', label: 'Vue extérieure 3D', icon: Satellite, testid: 'maison-viz-mode-exterior' },
  { id: 'building', label: 'Bâtiment 3D',       icon: Layers3,   testid: 'maison-viz-mode-building' },
  { id: 'shoebox',  label: 'Shoebox 3D / étage', icon: Box,      testid: 'maison-viz-mode-shoebox' },
  { id: 'orbit',    label: 'Tour 360°',          icon: RotateCw, testid: 'maison-viz-mode-orbit' },
  { id: 'svg',      label: 'Composite (legacy)', icon: ImageIcon, testid: 'maison-viz-mode-svg' },
];

const MaisonVisualizers = ({ projectId }) => {
  const [mode, setMode] = useState('exterior');
  const [floors, setFloors] = useState([]);
  const [activeFloorId, setActiveFloorId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exteriorPayload, setExteriorPayload] = useState(null);
  const [showSatellite, setShowSatellite] = useState(false);
  const [satAlign, setSatAlign] = useState({ rotation_deg: 0, offset_x_m: 0, offset_z_m: 0 });
  const [explodedGap, setExplodedGap] = useState(0); // Sprint 34 — meters between floors

  // Load saved satellite alignment once
  useEffect(() => {
    let ok = true;
    axios.get(`${API}/projects/${projectId}`, { withCredentials: true }).then((r) => {
      if (!ok) return;
      const a = (r.data?.general_info || {}).satellite_alignment;
      if (a) setSatAlign({
        rotation_deg: Number(a.rotation_deg) || 0,
        offset_x_m: Number(a.offset_x_m) || 0,
        offset_z_m: Number(a.offset_z_m) || 0,
      });
    }).catch(() => {});
    return () => { ok = false; };
  }, [projectId]);

  const saveAlignment = async (patch) => {
    const next = { ...satAlign, ...patch };
    setSatAlign(next);
    try {
      await axios.patch(`${API}/projects/${projectId}/satellite-alignment`, patch, { withCredentials: true });
    } catch { /* silent, keep local */ }
  };

  // Fetch the lots once when the component mounts — floors are derived from
  // the denormalized fields on each lot (floor_id, floor_name, floor_level),
  // so a single round-trip is enough. We need floors at any time the user
  // could click the PDF export button (which is always visible).
  useEffect(() => {
    if (floors.length > 0) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const lotsRes = await axios.get(
          `${API}/projects/${projectId}/lots`,
          { withCredentials: true });
        const lots = lotsRes.data?.items || lotsRes.data || [];
        // Group by floor_id, picking the lot with the richest structure (most walls).
        const byFloor = {};
        for (const l of lots) {
          const fid = l.floor_id;
          if (!fid) continue;
          const pse = l.plan_source_editor || l.plan_source_state || {};
          const wallCount = (pse.structure?.walls || []).length;
          const prevPse = byFloor[fid]?.plan_source_editor || byFloor[fid]?.plan_source_state || {};
          if (!byFloor[fid] || wallCount > (prevPse.structure?.walls || []).length) {
            byFloor[fid] = l;
          }
        }
        const items = Object.entries(byFloor).map(([fid, lot]) => {
          const f = lotToFloor({
            floor_id: fid,
            name:  lot.floor_name || `Niveau ${lot.floor_level ?? 0}`,
            code:  (lot.floor_name || '').toUpperCase().split(' ')[0] || `N${lot.floor_level ?? 0}`,
            level: Number.isFinite(lot.floor_level) ? lot.floor_level : 0,
            // Sprint 30 — HSP réelle (denormalisée par /lots).
            hsp_m: Number.isFinite(lot.floor_hsp_m) && lot.floor_hsp_m > 0
              ? lot.floor_hsp_m : undefined,
            height_m: lot.floor_height_m || undefined,
          }, lot);
          f.lot_id = lot.lot_id;  // Sprint 33 — carry the lot for tour launch
          return f;
        });
        items.sort((a, b) => b.level - a.level);
        if (!cancelled) {
          setFloors(items);
          if (items.length && !activeFloorId) {
            // Default to the RDC if any, else the lowest-level floor we have.
            const rdc = items.find((f) => f.level === 0) || items[items.length - 1];
            setActiveFloorId(rdc.floor_id);
          }
        }
      } catch (err) {
        if (!cancelled) setError(formatApiError(err, 'Impossible de charger les étages'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, activeFloorId, floors.length]);

  // Fetch the exterior payload when entering exterior mode.
  useEffect(() => {
    if (mode !== 'exterior') return;
    if (exteriorPayload) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/projects/${projectId}/maison/exterior`,
          { withCredentials: true });
        if (!cancelled) setExteriorPayload(data);
      } catch (err) {
        if (!cancelled) setError(formatApiError(err, 'Vue extérieure indisponible'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, projectId, exteriorPayload]);

  const activeFloor = useMemo(
    () => floors.find((f) => f.floor_id === activeFloorId) || floors[0] || null,
    [floors, activeFloorId]);

  return (
    <div className="rounded-lg border border-white/10 bg-black/30"
         data-testid="maison-visualizers-root">
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10 flex-wrap">
        <span className="text-[10.5px] uppercase tracking-[0.2em] text-emerald-300/80">
          Visualisation Maison 3D
        </span>
        <div className="w-px h-4 bg-white/10 mx-1.5" />
        {MODE_OPTIONS.map((o) => {
          const Icon = o.icon;
          const active = mode === o.id;
          return (
            <button key={o.id} type="button" onClick={() => setMode(o.id)}
                    data-testid={o.testid}
                    aria-pressed={active}
                    className={`inline-flex items-center gap-1 h-7 px-2 rounded text-[10.5px] border transition-colors ${
                      active
                        ? 'bg-emerald-500/20 border-emerald-400/70 text-emerald-100'
                        : 'bg-black/30 border-white/10 text-gray-300 hover:bg-white/5'
                    }`}>
              <Icon className="w-3 h-3" />
              {o.label}
            </button>
          );
        })}
        {(mode === 'shoebox' || mode === 'orbit') && floors.length > 0 && (
          <>
            <div className="w-px h-4 bg-white/10 mx-1.5" />
            <span className="text-[10px] text-gray-400 uppercase tracking-[0.15em]">Étage :</span>
            {floors.map((f) => {
              const active = f.floor_id === activeFloorId;
              return (
                <button key={f.floor_id} type="button"
                        onClick={() => setActiveFloorId(f.floor_id)}
                        data-testid={`maison-viz-floor-${f.code}`}
                        className={`h-7 px-2 rounded text-[10.5px] border transition-colors ${
                          active
                            ? 'bg-amber-500/20 border-amber-400/70 text-amber-100'
                            : 'bg-black/30 border-white/10 text-gray-300 hover:bg-white/5'
                        }`}>
                  {f.code || f.name}
                </button>
              );
            })}
          </>
        )}
        <div className="flex-1" />
        {/* Sprint 27 — Dossier PDF Flo X. Disponible dès que floors+exterior chargés. */}
        <MaisonPdfExportButton
          projectId={projectId}
          floors={floors}
          exteriorPayload={exteriorPayload}
          testId="maison-viz-pdf-export-btn"
        />
      </div>

      <div className="p-3" style={{ minHeight: 480 }}>
        {error && (
          <div className="flex items-center gap-2 text-amber-300 text-[12px] px-3 py-2 rounded
                          bg-amber-500/10 border border-amber-400/30 mb-3">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}
        {loading && (
          <div className="flex items-center justify-center text-gray-300 text-[12px] h-72">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Chargement…
          </div>
        )}
        {!loading && mode === 'exterior' && exteriorPayload && (
          <div className="overflow-hidden rounded border border-white/5" style={{ height: 520 }}>
            <Exterior3DViewer
              exteriorPayload={exteriorPayload}
              title={exteriorPayload?.name || 'Vue extérieure 3D'}
              subtitle={[
                exteriorPayload?.address,
                exteriorPayload?.terrain?.area_m2 && `Terrain ${exteriorPayload.terrain.area_m2.toFixed(0)} m²`,
                exteriorPayload?.footprint?.surface_m2 && `Bâti ${exteriorPayload.footprint.surface_m2.toFixed(0)} m²`,
              ].filter(Boolean).join(' · ')}
              className="w-full h-full"
              testId="maison-viz-exterior3d"
            />
          </div>
        )}
        {!loading && mode === 'building' && floors.length > 0 && (
          <div className="overflow-hidden rounded border border-white/5 relative" style={{ height: 520 }}>
            <BuildingStack3DViewer
              floors={floors}
              activeFloorId={activeFloorId}
              className="w-full h-full"
              testId="maison-viz-building3d"
              projectId={projectId}
              showSatellite={showSatellite}
              satelliteAlignment={satAlign}
              explodedGap={explodedGap}
            />
            {/* Sprint 34 — Exploded View toggle (top-left) */}
            <div
              className="absolute top-3 left-3 bg-black/60 border border-white/10 rounded-md px-2 py-1 flex items-center gap-2 shadow-lg"
              style={{ backdropFilter: 'blur(6px)' }}
              data-testid="maison-viz-exploded-controls"
            >
              <Layers3 className="w-3 h-3 text-emerald-300" />
              <span className="text-[10px] uppercase tracking-widest text-emerald-200">Exploded</span>
              <input
                type="range"
                min={0} max={5} step={0.5}
                value={explodedGap}
                onChange={(e) => setExplodedGap(Number(e.target.value))}
                className="w-24 h-1 accent-emerald-400"
                data-testid="exploded-gap-slider"
              />
              <span className="text-[10.5px] text-gray-200 font-mono">{explodedGap.toFixed(1)}m</span>
            </div>
            {showSatellite && (
              <SatelliteAlignmentControls value={satAlign} onChange={saveAlignment} floor={activeFloor} />
            )}
            <button
              type="button"
              onClick={() => setShowSatellite((v) => !v)}
              className={`absolute top-3 right-3 px-2.5 py-1 text-[11px] rounded border inline-flex items-center gap-1.5 ${showSatellite ? 'bg-amber-500 text-black border-amber-400' : 'bg-black/50 text-gray-200 border-white/10 hover:border-amber-400'}`}
              data-testid="maison-viz-satellite-toggle"
              title="Afficher la vue satellite au sol"
            >
              <Satellite className="w-3 h-3" /> Satellite {showSatellite ? 'ON' : 'OFF'}
            </button>
          </div>
        )}
        {!loading && mode === 'shoebox' && activeFloor && (
          <div className="overflow-hidden rounded border border-white/5 relative" style={{ height: 520 }}>
            <Shoebox3DViewer
              floor={activeFloor}
              showGrid
              showTerrain
              className="w-full h-full"
              testId="maison-viz-shoebox3d"
              projectId={projectId}
              showSatellite={showSatellite}
              satelliteAlignment={satAlign}
            />
            {showSatellite && (
              <SatelliteAlignmentControls value={satAlign} onChange={saveAlignment} floor={activeFloor} />
            )}
            <button
              type="button"
              onClick={() => setShowSatellite((v) => !v)}
              className={`absolute top-3 right-3 px-2.5 py-1 text-[11px] rounded border inline-flex items-center gap-1.5 ${showSatellite ? 'bg-amber-500 text-black border-amber-400' : 'bg-black/50 text-gray-200 border-white/10 hover:border-amber-400'}`}
              data-testid="maison-viz-satellite-toggle-shoebox"
              title="Afficher la vue satellite au sol"
            >
              <Satellite className="w-3 h-3" /> Satellite {showSatellite ? 'ON' : 'OFF'}
            </button>
          </div>
        )}
        {!loading && mode === 'shoebox' && !activeFloor && (
          <div className="flex items-center justify-center text-gray-400 text-[12px] h-72">
            Aucun étage avec géométrie — dessinez vos murs dans l&apos;onglet « 2 · Structure ».
          </div>
        )}
        {/* Sprint 33 — Tour 360° hybride (4 sprites + Sora video optionnelle). */}
        {!loading && mode === 'orbit' && activeFloor?.lot_id && (
          <OrbitTourViewer
            lotId={activeFloor.lot_id}
            projectId={projectId}
          />
        )}
        {!loading && mode === 'orbit' && !activeFloor?.lot_id && (
          <div className="flex items-center justify-center text-gray-400 text-[12px] h-72">
            Sélectionne un étage avec un lot pour générer sa tour 360°.
          </div>
        )}
        {mode === 'svg' && (
          <ExteriorPreview projectId={projectId} />
        )}
      </div>
    </div>
  );
};

export default MaisonVisualizers;

// Sprint 32.5 — Compact alignment controls for the satellite ground plane
// Sprint 32.7 — Auto-align rotation from the longest wall of the active floor
function SatelliteAlignmentControls({ value, onChange, floor }) {
  const walls = (floor?.geometry?.walls || floor?.walls || []);
  const longestWall = walls.length > 0
    ? walls.reduce((best, w) => {
        const len = Math.hypot((w.x2 - w.x1), (w.y2 - w.y1));
        return len > (best?._len || 0) ? { ...w, _len: len } : best;
      }, null)
    : null;

  const autoAlign = () => {
    if (!longestWall) {
      toast.error('Aucun mur trouvé sur cet étage');
      return;
    }
    const dx = longestWall.x2 - longestWall.x1;
    const dy = longestWall.y2 - longestWall.y1;
    const angleRad = Math.atan2(dy, dx);
    let angleDeg = angleRad * 180 / Math.PI;
    // Normalize to [-180, 180]
    while (angleDeg > 180) angleDeg -= 360;
    while (angleDeg < -180) angleDeg += 360;
    onChange({ rotation_deg: Math.round(angleDeg * 10) / 10 });
    toast.success(`Rotation dérivée du mur principal (${walls.length} mur${walls.length > 1 ? 's' : ''} détecté${walls.length > 1 ? 's' : ''})`);
  };

  return (
    <div
      className="absolute bottom-3 right-3 bg-black/70 border border-white/10 rounded-md px-3 py-2 text-[11px] text-gray-200 shadow-xl space-y-1.5"
      style={{ backdropFilter: 'blur(6px)', width: 240 }}
      data-testid="satellite-alignment-controls"
    >
      <div className="flex items-center gap-1.5 text-amber-300 uppercase tracking-widest text-[9.5px]">
        <Compass className="w-3 h-3" /> Alignement satellite
      </div>
      <button
        type="button"
        onClick={autoAlign}
        disabled={!longestWall}
        className="w-full text-[10.5px] bg-emerald-500/20 hover:bg-emerald-500/30 disabled:opacity-40 border border-emerald-500/40 rounded py-1 text-emerald-100 flex items-center justify-center gap-1"
        data-testid="satellite-auto-align"
        title="Utilise le mur le plus long de l'étage courant comme façade nord"
      >
        <Compass className="w-3 h-3" /> Aligner nord auto ({walls.length} murs)
      </button>
      <label className="block">
        <div className="flex justify-between text-[10px] text-gray-400">
          <span>Rotation nord</span><span className="font-mono">{Math.round(value.rotation_deg)}°</span>
        </div>
        <input
          type="range" min={-180} max={180} step={1}
          value={value.rotation_deg}
          onChange={(e) => onChange({ rotation_deg: Number(e.target.value) })}
          className="w-full h-1 accent-amber-400"
          data-testid="satellite-rotation-slider"
        />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <div className="text-[10px] text-gray-400">Offset X (m)</div>
          <input
            type="number" step={0.5}
            value={Number(value.offset_x_m).toFixed(1)}
            onChange={(e) => onChange({ offset_x_m: Number(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 rounded px-1.5 py-0.5 text-[11px] font-mono"
            data-testid="satellite-offset-x"
          />
        </label>
        <label className="block">
          <div className="text-[10px] text-gray-400">Offset Z (m)</div>
          <input
            type="number" step={0.5}
            value={Number(value.offset_z_m).toFixed(1)}
            onChange={(e) => onChange({ offset_z_m: Number(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 rounded px-1.5 py-0.5 text-[11px] font-mono"
            data-testid="satellite-offset-z"
          />
        </label>
      </div>
      <button
        type="button"
        onClick={() => onChange({ rotation_deg: 0, offset_x_m: 0, offset_z_m: 0 })}
        className="w-full text-[10px] text-gray-400 hover:text-white py-0.5"
        data-testid="satellite-alignment-reset"
      >
        ↺ Réinitialiser
      </button>
    </div>
  );
}